import React, { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './CustomSlider.scss';

export interface SliderMarker {
  value: number;
  label: string;
}

export type SliderRange = [number, number];

interface CustomSliderBaseProps {
  min: number;
  max: number;
  markers?: SliderMarker[];
  baseStep?: number;
  disabled?: boolean;
}

interface CustomSliderSingleProps extends CustomSliderBaseProps {
  isRange: false;
  value: number;
  onChange: (value: number) => void;
  showTooltip?: boolean;
}

interface CustomSliderRangeProps extends CustomSliderBaseProps {
  isRange?: true;
  value: SliderRange;
  onChange: (value: SliderRange) => void;
}

type CustomSliderProps = CustomSliderSingleProps | CustomSliderRangeProps;

const INTERNAL_MAX = 100;
const DEFAULT_BASE_STEP = 5000;

const clamp = (value: number, lo: number, hi: number): number =>
  Math.min(Math.max(value, lo), hi);

const calculatePercentage = (value: number, min: number, max: number): number =>
  Math.sqrt((clamp(value, min, max) - min) / (max - min)) * INTERNAL_MAX;

const calculateValue = (pct: number, min: number, max: number, step: number): number => {
  if (pct <= 0) return min;
  if (pct >= INTERNAL_MAX) return max;
  const raw = min + (max - min) * Math.pow(pct / INTERNAL_MAX, 2);
  return clamp(Math.round(raw / step) * step, min, max);
};

export const normalizeSliderRange = (range: SliderRange): SliderRange => {
  const [a, b] = Array.isArray(range) ? range : [range, range];
  const safeA = Number.isFinite(a) ? a : 0;
  const safeB = Number.isFinite(b) ? b : safeA;
  return safeA <= safeB ? [safeA, safeB] : [safeB, safeA];
};

const formatSliderValue = (value: number, locale: string): string =>
  value.toLocaleString(locale);

const SliderMarkers: React.FC<{
  markers: SliderMarker[];
  min: number;
  max: number;
}> = ({ markers, min, max }) => {
  const mapped = useMemo(
    () => markers.map((m) => ({ ...m, pct: calculatePercentage(m.value, min, max) })),
    [markers, min, max],
  );

  return (
    <div className="custom-slider__markers">
      {mapped.map((m) => (
        <div
          key={`${m.value}-${m.label}`}
          className="custom-slider__marker-item"
          style={{ '--marker-percent': `${m.pct}%` } as React.CSSProperties}
        >
          <div className="custom-slider__marker-line" />
          <span className="custom-slider__marker-label">{m.label}</span>
        </div>
      ))}
    </div>
  );
};

const SingleSlider: React.FC<Omit<CustomSliderSingleProps, 'isRange'>> = ({
  min,
  max,
  value,
  onChange,
  markers = [],
  showTooltip = true,
  baseStep = DEFAULT_BASE_STEP,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const percentage = useMemo(
    () => calculatePercentage(value, min, max),
    [value, min, max],
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(calculateValue(Number(e.target.value), min, max, baseStep));
  };

  const handlePointerDown = () => {
    globalThis.getSelection()?.removeAllRanges();
  };

  const sliderStyles = {
    '--slider-fill': `${percentage}%`,
  } as React.CSSProperties;

  return (
    <div className="custom-slider" style={sliderStyles}>
      <div className="custom-slider__track-container">
        <input
          type="range"
          className="custom-slider__input"
          min={0}
          max={INTERNAL_MAX}
          step={0.1}
          value={percentage}
          onChange={handleChange}
          onPointerDown={handlePointerDown}
          onDragStart={(e) => e.preventDefault()}
          disabled={disabled}
        />
        <div className="custom-slider__halo" />

        {showTooltip && (
          <div className="custom-slider__tooltip">
            {value >= max ? t('globalFilter.maxLabel') : value}
          </div>
        )}

        {markers.length > 0 && <SliderMarkers markers={markers} min={min} max={max} />}
      </div>
    </div>
  );
};

const RangeSlider: React.FC<Omit<CustomSliderRangeProps, 'isRange'>> = ({
  min,
  max,
  value,
  onChange,
  markers = [],
  baseStep = DEFAULT_BASE_STEP,
  disabled = false,
}) => {
  const { t, i18n } = useTranslation();
  const [activeThumb, setActiveThumb] = useState<'start' | 'end' | null>(null);

  const [startVal, endVal] = normalizeSliderRange(value);
  const startPct = calculatePercentage(startVal, min, max);
  const endPct = calculatePercentage(endVal, min, max);

  const sliderStyles = {
    '--slider-fill-start': `${startPct}%`,
    '--slider-fill-end': `${endPct}%`,
  } as React.CSSProperties;

  const markerValues = useMemo(() => new Set(markers.map((m) => m.value)), [markers]);

  const valueLabels = useMemo(() => {
    const unique = startVal === endVal ? [startVal] : [startVal, endVal];
    return unique
      .filter((v) => !markerValues.has(v))
      .map((v) => ({ value: v, pct: calculatePercentage(v, min, max) }));
  }, [startVal, endVal, markerValues, min, max]);

  const handleStartChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = calculateValue(Number(e.target.value), min, max, baseStep);
    onChange([Math.min(next, endVal), endVal]);
  };

  const handleEndChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = calculateValue(Number(e.target.value), min, max, baseStep);
    onChange([startVal, Math.max(next, startVal)]);
  };

  const handleStartPointerDown = () => {
    globalThis.getSelection()?.removeAllRanges();
    setActiveThumb('start');
  };

  const handleEndPointerDown = () => {
    globalThis.getSelection()?.removeAllRanges();
    setActiveThumb('end');
  };
  
  const handlePointerUp = () => setActiveThumb(null);

  const startOnTop = activeThumb ? activeThumb === 'start' : endPct >= INTERNAL_MAX - 0.5;

  return (
    <div className="custom-slider custom-slider--range" style={sliderStyles}>
      <div className="custom-slider__track-container">
        <input
          type="range"
          className={`custom-slider__input custom-slider__input--range${startOnTop ? ' custom-slider__input--on-top' : ''}`}
          min={0}
          max={INTERNAL_MAX}
          step={0.1}
          value={startPct}
          onChange={handleStartChange}
          onPointerDown={handleStartPointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDragStart={(e) => e.preventDefault()}
          aria-label={t('globalFilter.rangeStartLabel')}
          disabled={disabled}
        />
        <div className="custom-slider__halo custom-slider__halo--start" />

        <input
          type="range"
          className={`custom-slider__input custom-slider__input--range${startOnTop ? '' : ' custom-slider__input--on-top'}`}
          min={0}
          max={INTERNAL_MAX}
          step={0.1}
          value={endPct}
          onChange={handleEndChange}
          onPointerDown={handleEndPointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDragStart={(e) => e.preventDefault()}
          aria-label={t('globalFilter.rangeEndLabel')}
          disabled={disabled}
        />
        <div className="custom-slider__halo custom-slider__halo--end" />

        {markers.length > 0 && <SliderMarkers markers={markers} min={min} max={max} />}

        {valueLabels.length > 0 && (
          <div className="custom-slider__values">
            {valueLabels.map((item) => (
              <div
                key={item.value}
                className="custom-slider__value-item"
                style={{ '--value-percent': `${item.pct}%` } as React.CSSProperties}
              >
                <span className="custom-slider__value-text">
                  {formatSliderValue(item.value, i18n.language)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const CustomSlider: React.FC<CustomSliderProps> = (props) =>
  props.isRange === false ? <SingleSlider {...props} /> : <RangeSlider {...props} />;